import {
  HOMEOWNER_DIGEST_LOANS_MODAL,
  HOMEOWNER_DIGEST_LOANS_MODAL_DELETE_ALERT
} from '../../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { Format } from '../../../..'
import { CurrentLoan } from '../../../../../types/loan'
import { deleteHomeLoan, restoreHomeLoan } from '../../../../../actions/home'
import { markLoansChecked } from '../../../../../actions/homeowner'
import { editBalance, editLoan } from '../../../../../actions/loan'
import { LOAN_STATUS, LOAN_TERM_MESSAGES, LOAN_TYPE } from '../../../../../constants/loan'
import { Flex, Text, Button, Heading, ButtonGroup, Box, IconButton, Icon } from '@homebotapp/hb-react-component-catalog'
import { addMonths, isAfter, startOfMonth } from 'date-fns'
import { selectPaidInFull } from '../../../../../store/selectors/home/answers'
import { selectByStatus, selectFirstCEL } from '../../../../../store/selectors/home/loans'

export const MSG = defineMessages({
  heloc: {
    id: 'Mortgages.loans.term.heloc',
    defaultMessage: 'HELOC'
  },
  rate: {
    id: 'Mortgages.loans.rate',
    defaultMessage: '{term} at {rate}%'
  },
  refi: {
    id: 'Mortgages.loans.refi',
    defaultMessage: 'Refi'
  },
  lender: {
    id: 'Mortgages.loans.lender',
    defaultMessage: 'from {lender}'
  },
  verify: {
    id: 'Mortgages.loans.verify',
    defaultMessage: 'Are these numbers correct?'
  },
  deleteAlert: {
    id: 'Mortgages.loans.deleteAlert',
    defaultMessage: 'Are you sure you want to delete this loan?'
  },
  yes: {
    id: 'Mortgages.loans.correct',
    defaultMessage: 'Yes'
  },
  no: {
    id: 'Mortgages.loans.incorrect',
    defaultMessage: 'No'
  },
  helocDisclaimer: {
    id: 'Mortgages.disclaimer.heloc',
    defaultMessage: "Note: HELOC's not used in report calculations yet"
  },
  edit: {
    id: 'Mortgages.loans.edit',
    defaultMessage: 'Edit'
  },
  delete: {
    id: 'Mortgages.loans.deleteButton',
    defaultMessage: 'Delete'
  },
  restoreLabel: {
    id: 'Mortgages.loans.deleteLabel',
    defaultMessage: 'Delete current loan in order to restore this one'
  },
  restore: {
    id: 'Mortgages.loans.restoreButton',
    defaultMessage: 'Restore'
  },
  bal: {
    id: 'Mortgages.loans.currentBalance',
    defaultMessage: 'Current Balance: {balance}'
  },
  update: {
    id: 'Mortgages.loans.updateBalance',
    defaultMessage: 'Update balance'
  }
})

export interface LoanProps {
  loan: CurrentLoan
}

export const Loan = ({ loan }: LoanProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const { current } = useSelector(selectByStatus)
  const paidInFull = useSelector(selectPaidInFull)
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const loanOriginationDate = useSelector(selectFirstCEL)?.date?.split('-').join('/')
  const firstPaymentDate = addMonths(startOfMonth(new Date(loanOriginationDate)), 2)
  const hasValidLoan = isAfter(new Date(), firstPaymentDate) && !paidInFull
  const needsConfirmed = loan?.status === LOAN_STATUS.CURRENT && (!loan?.termYears || !loan?.rate)
  const currentLoanWithSameTypeExists = current?.find(l => l?.loanType === loan?.loanType) ? true : false

  useEffect(() => {
    if (!needsConfirmed) setLoading(false)
  }, [needsConfirmed])

  const handleDelete = async () => {
    setLoading(true)
    await dispatch(deleteHomeLoan(loan))
    setLoading(false)
  }

  const handleRestore = async () => {
    setLoading(true)
    await dispatch(restoreHomeLoan(loan.loanId, loan.homeId))
    setLoading(false)
  }

  const getReadableTerm = () =>
    loan?.loanType === 'HELOC'
      ? intl.formatMessage(MSG.heloc)
      : loan?.adjustable
      ? intl.formatMessage(LOAN_TERM_MESSAGES.arm, { years: loan?.armYearsInitial || 5 })
      : intl.formatMessage(LOAN_TERM_MESSAGES.fix, { years: loan?.termYears || loan?.termYearsEstimated })

  return (
    <Flex
      pr={6}
      py={4}
      gap={4}
      flex='1 1 auto'
      borderRadius='.25rem'
      justify='space-between'
      direction={['column', null, 'row']}
      // @ts-ignore
      backgroundColor='var(--brand-surface-layer-50)'
    >
      <Box>
        <Heading mb={0} as='h3' size='sm' textAlign='left' fontWeight='light' textTransform='capitalize'>
          <Format.Currency value={loan?.amount} />
          {' - '}
          <Format.Date value={loan?.date} format='MMMM YYYY' />
        </Heading>
        <Text mb={0} size='xs'>
          {intl.formatMessage(MSG.rate, { term: getReadableTerm(), rate: loan?.rate || loan?.rateEstimated })}
        </Text>
        {(loan?.refi || loan?.lender) && (
          <Text mb={0} size='2xs'>
            {loan?.refi && `${intl.formatMessage(MSG.refi)} `}
            {loan?.lender && intl.formatMessage(MSG.lender, { lender: loan.lender })}
          </Text>
        )}
        {(needsConfirmed || loan?.loanType === LOAN_TYPE.HELOC) && (
          <Box>
            {needsConfirmed && (
              <>
                <Text mt={2} size='2xs' color='warning'>
                  {intl.formatMessage(MSG.verify)}
                </Text>
                <ButtonGroup>
                  <Button
                    w={16}
                    size='xs'
                    isLoading={loading}
                    onClick={() => {
                      setLoading(true)
                      dispatch(markLoansChecked([loan]))
                    }}
                    tracking={{
                      guid: '6AucA9xMDS5ytxFPFKawPq',
                      ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
                      descriptor: MSG.yes.defaultMessage
                    }}
                  >
                    {intl.formatMessage(MSG.yes)}
                  </Button>
                  <Button
                    w={16}
                    size='xs'
                    isDisabled={loading}
                    onClick={() => dispatch(editLoan(loan))}
                    tracking={{
                      guid: 'rJMi6KcVMHc2fYZptBsdXX',
                      ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
                      descriptor: MSG.no.defaultMessage
                    }}
                  >
                    {intl.formatMessage(MSG.no)}
                  </Button>
                </ButtonGroup>
              </>
            )}
            {loan?.loanType === LOAN_TYPE.HELOC && (
              <Text size='2xs' color='warning' fontStyle='italic'>
                {intl.formatMessage(MSG.helocDisclaimer)}
              </Text>
            )}
          </Box>
        )}
        {showDeleteAlert && (
          <>
            <Text mt={2} size='2xs' color='warning' fontStyle='italic'>
              {intl.formatMessage(MSG.deleteAlert)}
            </Text>
            <ButtonGroup>
              <Button
                w={16}
                size='xs'
                isLoading={loading}
                colorScheme='danger'
                onClick={handleDelete}
                tracking={{
                  guid: 'jWWvQDpvYZ11K4fms9oqcn',
                  ui_context: HOMEOWNER_DIGEST_LOANS_MODAL_DELETE_ALERT,
                  descriptor: MSG.yes.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.yes)}
              </Button>
              <Button
                w={16}
                size='xs'
                isDisabled={loading}
                onClick={() => setShowDeleteAlert(false)}
                tracking={{
                  guid: 'oeBagheS8Zia6GvtBwjF63',
                  ui_context: HOMEOWNER_DIGEST_LOANS_MODAL_DELETE_ALERT,
                  descriptor: MSG.no.defaultMessage
                }}
              >
                {intl.formatMessage(MSG.no)}
              </Button>
            </ButtonGroup>
          </>
        )}
        {loan.status === 'deleted' && (
          <>
            {currentLoanWithSameTypeExists && (
              <Text my={1} size='2xs' color='warning' fontStyle='italic'>
                {intl.formatMessage(MSG.restoreLabel)}
              </Text>
            )}
            <Button
              mt={1}
              size='xs'
              isLoading={loading}
              colorScheme='primary'
              onClick={handleRestore}
              data-qa='restore-loan-button'
              isDisabled={currentLoanWithSameTypeExists}
              tracking={{
                guid: '1YgeFU7jpuxJy4QRRjZUb7',
                ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
                descriptor: MSG.restore.defaultMessage
              }}
            >
              {intl.formatMessage(MSG.restore)}
            </Button>
          </>
        )}
        {!needsConfirmed && !showDeleteAlert && loan.status !== 'deleted' && (
          <ButtonGroup mt={2}>
            <Button
              w={16}
              size='xs'
              data-qa='edit-loan-details-button'
              onClick={() => dispatch(editLoan(loan))}
              tracking={{
                guid: '6h1QLkb6a7B8Stsrz6giVa',
                ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
                descriptor: MSG.edit.defaultMessage
              }}
            >
              {intl.formatMessage(MSG.edit)}
            </Button>
            <Button
              w={16}
              size='xs'
              colorScheme='danger'
              onClick={() => setShowDeleteAlert(true)}
              data-qa='delete-loan-button'
              tracking={{
                guid: 'h7KnwDZc5wqJj7xwQWsgz8',
                ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
                descriptor: MSG.delete.defaultMessage
              }}
            >
              {intl.formatMessage(MSG.delete)}
            </Button>
          </ButtonGroup>
        )}
      </Box>
      {loan?.loanType !== LOAN_TYPE.HELOC && loan?.status === LOAN_STATUS.CURRENT && hasValidLoan && (
        <Flex gap={1}>
          <Text as='span' size='2xs'>
            {intl.formatMessage(MSG.bal, {
              balance: (
                <Text as='span' size='xs' color='success'>
                  <Format.Currency
                    value={
                      loan?.balance && loan.balance < loan?.progress?.principalLeft
                        ? loan.balance
                        : loan?.progress?.principalLeft
                    }
                    format='0,0'
                  />
                </Text>
              )
            })}
          </Text>
          <IconButton
            h={5}
            size='xs'
            variant='link'
            colorScheme='primary'
            icon={<Icon name='pencil' />}
            onClick={() => dispatch(editBalance(loan))}
            aria-label={intl.formatMessage(MSG.update)}
            tracking={{
              guid: 'gCkKCffdVFEq9Ai5nYvZ5m',
              ui_context: HOMEOWNER_DIGEST_LOANS_MODAL,
              descriptor: MSG.update.defaultMessage
            }}
          />
        </Flex>
      )}
    </Flex>
  )
}
